import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Recycle, Factory, Droplets, Sun, TreePine } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";

const PRINCIPLES = [
  { Icon: Factory, title: "Factory efficiency", desc: "Controlled production cuts material waste by up to 60% versus on-site construction." },
  { Icon: Recycle, title: "Circular materials", desc: "Steel frames and panel systems are fully recyclable at end of life — nothing goes to landfill." },
  { Icon: Leaf, title: "Lower embodied carbon", desc: "Optimized structures use less concrete and steel per square foot than conventional builds." },
  { Icon: Droplets, title: "Water-light sites", desc: "Dry construction methods eliminate the thousands of litres a wet site consumes." },
  { Icon: Sun, title: "Passive performance", desc: "Rockwool insulation and glazing tuned per climate — thermal comfort with less energy." },
  { Icon: TreePine, title: "Zero-cut foundations", desc: "Stilt and block foundations leave land, roots and habitats undisturbed." },
];

export default function Sustainability() {
  return (
    <PageWrapper>
      {/* Hero */}
      <section className="relative pt-[76px]" data-testid="sustainability-hero">
        <div className="relative h-[420px] md:h-[520px] overflow-hidden">
          <img src="/images/gen/sustainability-home.jpg" alt="Sustainable NUVUN home with green roof" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/55 to-[#0A0908]/25" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 pb-12 w-full">
              <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">Sustainability</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3] max-w-3xl">
                Luxury that treads <span className="italic text-[#D9A441]">lightly.</span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-[#D9A441]/15" data-testid="sustainability-stats">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#D9A441]/12">
          {[["60%", "Less material waste"], ["45%", "Faster than site builds"], ["100%", "Recyclable steel frames"], ["50+ yr", "Design lifespan"]].map(([v, l]) => (
            <div key={l} className="py-8 text-center">
              <div className="font-condensed text-3xl md:text-4xl text-[#D9A441]">{v}</div>
              <div className="eyebrow !text-[9px] text-[#F2EDE3]/50 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 lg:py-28" data-testid="sustainability-principles">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp>
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">Design Principles</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F2EDE3] max-w-2xl">
              Sustainable by design, <span className="italic text-[#F2EDE3]/70">not by disclaimer.</span>
            </h2>
          </FadeUp>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRINCIPLES.map(({ Icon, title, desc }, i) => (
              <FadeUp key={title} delay={i * 0.05}>
                <div className="panel p-7 h-full hover:border-[#D9A441]/50 transition-colors duration-500">
                  <span className="w-11 h-11 rounded-full border border-[#D9A441]/45 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#D9A441]" />
                  </span>
                  <div className="font-display text-xl text-[#F2EDE3]">{title}</div>
                  <p className="text-sm text-[#F2EDE3]/55 mt-3 leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto band */}
      <section className="py-20 bg-[#0C0B09] border-y border-[#D9A441]/12" data-testid="sustainability-manifesto">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 text-center">
          <FadeUp>
            <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl text-[#F2EDE3] leading-[1.3]">
              "The most sustainable building is the one built <span className="italic text-[#D9A441]">once</span>,
              built <span className="italic text-[#D9A441]">right</span>, and loved for
              <span className="italic text-[#D9A441]"> fifty years.</span>"
            </blockquote>
            <p className="mt-6 text-sm text-[#F2EDE3]/50 max-w-xl mx-auto leading-relaxed">
              Aligned with UN Sustainable Development Goals 8, 9 and 12 — decent work,
              resilient infrastructure and responsible production.
            </p>
            <Link to="/contact" data-testid="sustainability-cta" className="btn-gold mt-10">
              Build Responsibly With Us <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
